<template>
  <div class="sticky-table-header" ref="container" @click="onClick" />
</template>

<script>
const MOBILE_BREAKPOINT = 800

export default {
  data () {
    return {
      animationFrame: null,
      viewportTop: 0,
      pageHeader: null,
      tableHeader: null,
      scrollContainer: null,
      clonedTable: null,
      pageHeaderResizeObserver: null,
      tableHeaderResizeObserver: null,
    }
  },
  mounted () {
    window.addEventListener('scroll', this.scheduleVisibilityUpdate, { passive: true })
    window.addEventListener('resize', this.update)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.scheduleVisibilityUpdate)
    window.removeEventListener('resize', this.update)
    this.scrollContainer?.removeEventListener('scroll', this.syncHorizontalPosition)
    this.pageHeaderResizeObserver?.disconnect()
    this.tableHeaderResizeObserver?.disconnect()
    if (this.animationFrame) {
      window.cancelAnimationFrame(this.animationFrame)
    }
  },
  methods: {
    refresh (pageHeader, tableHeader) {
      if (!pageHeader || !tableHeader) {
        this.$refs.container.style.display = 'none'
        return
      }

      this.observePageHeader(pageHeader)
      this.observeTableHeader(tableHeader)
      this.observeScrollContainer(tableHeader.closest('.md-table'))
      this.pageHeader = pageHeader
      this.tableHeader = tableHeader
      this.update()
    },
    observePageHeader (pageHeader) {
      if (pageHeader === this.pageHeader) {
        return
      }

      this.pageHeaderResizeObserver?.disconnect()
      this.pageHeaderResizeObserver = new ResizeObserver(this.update)
      this.pageHeaderResizeObserver.observe(pageHeader)
    },
    observeTableHeader (tableHeader) {
      if (tableHeader === this.tableHeader) {
        return
      }

      this.tableHeaderResizeObserver?.disconnect()
      this.tableHeaderResizeObserver = new ResizeObserver(() => {
        this.build()
        this.updateVisibility()
      })
      this.tableHeaderResizeObserver.observe(tableHeader)
    },
    observeScrollContainer (scrollContainer) {
      if (!scrollContainer || scrollContainer === this.scrollContainer) {
        return
      }

      this.scrollContainer?.removeEventListener('scroll', this.syncHorizontalPosition)
      this.scrollContainer = scrollContainer
      this.scrollContainer.addEventListener('scroll', this.syncHorizontalPosition, { passive: true })
    },
    update () {
      if (!this.pageHeader || !this.tableHeader || !this.scrollContainer) {
        return
      }

      if (document.body.clientWidth < MOBILE_BREAKPOINT) {
        this.$refs.container.style.display = 'none'
        return
      }

      const pageHeaderTop = parseFloat(window.getComputedStyle(this.pageHeader).top) || 0
      this.viewportTop = pageHeaderTop + this.pageHeader.offsetHeight
      this.build()
      this.updateVisibility()
    },
    build () {
      const originalTable = this.tableHeader?.closest('table')
      if (!originalTable) {
        return
      }

      const clonedHeader = this.tableHeader.cloneNode(true)
      Array.from(clonedHeader.children).forEach((cell, index) => {
        const width = this.tableHeader.children[index].getBoundingClientRect().width
        Object.assign(cell.style, {
          width: `${width}px`,
          minWidth: `${width}px`,
          maxWidth: `${width}px`,
        })

        const label = cell.querySelector('.md-table-head-label')
        if (label) {
          label.style.display = 'flex'
        }
      })

      let sourceElement = this.tableHeader
      let clonedHierarchy = clonedHeader
      while (sourceElement.parentElement && sourceElement.parentElement !== originalTable) {
        const clonedParent = sourceElement.parentElement.cloneNode(false)
        clonedParent.appendChild(clonedHierarchy)
        clonedHierarchy = clonedParent
        sourceElement = sourceElement.parentElement
      }

      const tableWidth = originalTable.getBoundingClientRect().width
      const clonedTable = originalTable.cloneNode(false)
      clonedTable.appendChild(clonedHierarchy)
      Object.assign(clonedTable.style, {
        width: `${tableWidth}px`,
        minWidth: `${tableWidth}px`,
        tableLayout: 'fixed',
        borderCollapse: 'collapse',
        borderSpacing: '0',
      })

      this.$refs.container.replaceChildren(clonedTable)
      this.clonedTable = clonedTable
      this.syncSelectAllState()
      this.syncHorizontalPosition()
    },
    syncSelectAllState () {
      const originalCheckbox = this.tableHeader?.querySelector('.select-all-checkbox')
      const stickyCheckbox = this.$refs.container?.querySelector('.select-all-checkbox')
      if (!originalCheckbox || !stickyCheckbox) {
        return
      }

      stickyCheckbox.className = originalCheckbox.className
      const originalInput = originalCheckbox.querySelector('input')
      const stickyInput = stickyCheckbox.querySelector('input')
      if (originalInput && stickyInput) {
        stickyInput.checked = originalInput.checked
        stickyInput.indeterminate = originalInput.indeterminate
      }
    },
    syncHorizontalPosition () {
      if (this.clonedTable && this.scrollContainer) {
        this.clonedTable.style.transform = `translate3d(-${this.scrollContainer.scrollLeft}px, 0, 0)`
      }
    },
    scheduleVisibilityUpdate () {
      if (this.animationFrame) {
        return
      }

      this.animationFrame = window.requestAnimationFrame(() => {
        this.updateVisibility()
        this.animationFrame = null
      })
    },
    updateVisibility () {
      const tableContent = this.tableHeader?.closest('.md-table-content')
      if (!this.tableHeader || !tableContent || !this.scrollContainer || document.body.clientWidth < MOBILE_BREAKPOINT) {
        return
      }

      const headerBounds = this.tableHeader.getBoundingClientRect()
      const tableBounds = tableContent.getBoundingClientRect()
      const scrollBounds = this.scrollContainer.getBoundingClientRect()
      const shouldShow = headerBounds.top <= this.viewportTop &&
        tableBounds.bottom > this.viewportTop + headerBounds.height

      this.$refs.container.style.display = shouldShow ? 'block' : 'none'
      if (shouldShow) {
        Object.assign(this.$refs.container.style, {
          top: `${this.viewportTop}px`,
          left: `${scrollBounds.left + this.scrollContainer.clientLeft}px`,
          width: `${this.scrollContainer.clientWidth}px`,
        })
      }
    },
    onClick (event) {
      event.preventDefault()
      const stickyCell = event.target.closest('.md-table-head')
      const cellIndex = Array.from(this.$refs.container.querySelectorAll('.md-table-head')).indexOf(stickyCell)
      const originalCell = this.tableHeader?.children[cellIndex]
      if (!originalCell) {
        return
      }

      if (event.target.closest('.md-checkbox')) {
        originalCell.querySelector('.md-checkbox input')?.click()
        this.$nextTick(this.syncSelectAllState)
      } else {
        originalCell.querySelector('.table-head--clickable')?.click()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.sticky-table-header {
  position: fixed;
  z-index: 10;
  display: none;
  overflow: hidden;
  background-color: white;
}
</style>
